<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { projects } from '../../data/projects'
import { sceneItems, sceneSlots } from '../../data/scene'
import { personalInfo } from '../../data/personal'
import { socialLinks, socialIconPaths } from '../../data/navigation'
import { useWorkView } from '../../composables/useWorkView'
import type { Project } from '../../types/projects'

const portrait = new URL('../../assets/images/avatar.webp', import.meta.url).href

/*
  Backdrop: outline words floating in place, grouped into three parallax
  depths. Deliberately NOT a marquee — nothing travels across the screen, so
  the composition stays stable and the only motion is a slow bob plus a few
  px of pointer drift.

  Grouping by depth (rather than one transform per word) means the rAF loop
  writes a single pair of CSS variables and each layer multiplies them by its
  own --d. One DOM write per frame regardless of word count.

  `top`/`left` are the word's CENTRE (each blob is translated -50%/-50%), so
  keep them within roughly 8%–88% / 8%–88%: the backdrop clips overflow, and
  an anchor nearer the edge than half the word's own width loses letters. The
  near layer also stays clear of the left third where the identity card lives,
  since it is the boldest.
*/
interface Blob {
  text: string
  top: string
  left: string
  size: string
  rot: string
  dur: string
  delay: string
}

const blobLayers: { d: string; cls: string; items: Blob[] }[] = [
  {
    d: '0.4',
    cls: 'far',
    items: [
      { text: 'Logistics', top: '11%', left: '47%', size: '1.9rem', rot: '-3deg', dur: '19s', delay: '0s' },
      { text: 'Kotlin', top: '26%', left: '86%', size: '1.7rem', rot: '4deg', dur: '23s', delay: '-6s' },
      { text: 'Type-safe', top: '69%', left: '38%', size: '2rem', rot: '2deg', dur: '21s', delay: '-11s' },
      { text: 'Expo', top: '87%', left: '71%', size: '1.8rem', rot: '-5deg', dur: '25s', delay: '-3s' },
      { text: 'Realm DB', top: '54%', left: '88%', size: '1.7rem', rot: '3deg', dur: '20s', delay: '-14s' },
      { text: 'Room SQLite', top: '40%', left: '30%', size: '1.8rem', rot: '-2deg', dur: '24s', delay: '-8s' },
    ],
  },
  {
    d: '0.95',
    cls: 'mid',
    items: [
      { text: 'ChiNet', top: '18%', left: '36%', size: '3rem', rot: '-2deg', dur: '17s', delay: '-2s' },
      { text: 'RaAz TMS', top: '45%', left: '82%', size: '2.7rem', rot: '3deg', dur: '20s', delay: '-9s' },
      { text: 'Moya', top: '76%', left: '52%', size: '2.9rem', rot: '-4deg', dur: '18s', delay: '-13s' },
      { text: 'Offline-first', top: '33%', left: '61%', size: '2.6rem', rot: '2deg', dur: '22s', delay: '-5s' },
      { text: 'Play Integrity', top: '87%', left: '35%', size: '2.8rem', rot: '3deg', dur: '19s', delay: '-16s' },
    ],
  },
  {
    d: '1.7',
    cls: 'near',
    items: [
      { text: 'Flutter', top: '7%', left: '68%', size: '4.4rem', rot: '-2deg', dur: '15s', delay: '-4s' },
      { text: 'React Native', top: '57%', left: '44%', size: '4rem', rot: '2deg', dur: '16s', delay: '-10s' },
      { text: 'Jetpack Compose', top: '82%', left: '83%', size: '4.6rem', rot: '-3deg', dur: '14s', delay: '-7s' },
      { text: 'TypeScript', top: '30%', left: '87%', size: '4.2rem', rot: '4deg', dur: '17s', delay: '-12s' },
    ],
  },
]

const { activeId, open } = useWorkView()

/** The 3D scene only exists above this width; below it, 2D fallback. */
const WIDE = '(min-width: 1000px)'

interface Screen {
  project: Project
  device: 'desktop' | 'phone'
  src: string
  /** Position in the full project list, for the caption number. */
  ordinal: number
}

/* Only projects that actually have a screenshot can appear in the scene. */
const screens = computed<Screen[]>(() =>
  sceneItems.flatMap((item) => {
    const project = projects.find((p) => p.id === item.id)
    if (!project) return []
    const src = project.images?.[item.shot ?? 0] ?? project.image
    if (!src) return []
    return [
      { project, device: item.device, src, ordinal: projects.indexOf(project) + 1 },
    ]
  })
)

const count = computed(() => screens.value.length)

/* ---------------- spline orbit engine ---------------- */
interface ParsedSlot {
  leftPct: number
  topPct: number
  zPx: number
  ryDeg: number
  rxDeg: number
  rzDeg: number
  scale: number
}

const parsedSlots: ParsedSlot[] = sceneSlots.map((s) => ({
  leftPct: parseFloat(s.left),
  topPct: parseFloat(s.top),
  zPx: parseFloat(s.z),
  ryDeg: parseFloat(s.ry),
  rxDeg: parseFloat(s.rx),
  rzDeg: parseFloat(s.rz),
  scale: parseFloat(s.s),
}))

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  return (
    0.5 *
    (2 * p1 +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t)
  )
}

function interpolateSlot(u: number): ParsedSlot {
  const N = parsedSlots.length
  const uMod = ((u % N) + N) % N
  const i = Math.floor(uMod)
  const t = uMod - i
  const p0 = parsedSlots[(i - 1 + N) % N]
  const p1 = parsedSlots[i]
  const p2 = parsedSlots[(i + 1) % N]
  const p3 = parsedSlots[(i + 2) % N]

  return {
    leftPct: catmullRom(p0.leftPct, p1.leftPct, p2.leftPct, p3.leftPct, t),
    topPct: catmullRom(p0.topPct, p1.topPct, p2.topPct, p3.topPct, t),
    zPx: catmullRom(p0.zPx, p1.zPx, p2.zPx, p3.zPx, t),
    ryDeg: catmullRom(p0.ryDeg, p1.ryDeg, p2.ryDeg, p3.ryDeg, t),
    rxDeg: catmullRom(p0.rxDeg, p1.rxDeg, p2.rxDeg, p3.rxDeg, t),
    rzDeg: catmullRom(p0.rzDeg, p1.rzDeg, p2.rzDeg, p3.rzDeg, t),
    scale: catmullRom(p0.scale, p1.scale, p2.scale, p3.scale, t),
  }
}

const stageSize = reactive({ w: 0, h: 0 })
const cardEls = ref<(HTMLElement | null)[]>([])
const leadIndex = ref(0)
const lead = computed<Screen | null>(() => screens.value[leadIndex.value] ?? null)

let orbitOffset = 0
let targetOrbitOffset: number | null = null
/** Celestial orbit speed: ~50s per complete 8-card revolution. */
const ORBIT_SPEED = 0.16

function computeCardCSS(u: number, stageW: number, stageH: number) {
  const s = interpolateSlot(u)
  const x = ((s.leftPct - 50) / 100) * stageW
  const y = ((s.topPct - 50) / 100) * stageH
  return {
    x: `${x.toFixed(1)}px`,
    y: `${y.toFixed(1)}px`,
    z: `${s.zPx.toFixed(1)}px`,
    ry: `${s.ryDeg.toFixed(2)}deg`,
    rx: `${s.rxDeg.toFixed(2)}deg`,
    rz: `${s.rzDeg.toFixed(2)}deg`,
    s: `${s.scale.toFixed(3)}`,
  }
}

function initialCardStyle(screenIndex: number) {
  const n = count.value || 1
  const u = ((screenIndex + orbitOffset) % n + n) % n
  const s = interpolateSlot(u)
  const x = stageSize.w ? ((s.leftPct - 50) / 100) * stageSize.w : 0
  const y = stageSize.h ? ((s.topPct - 50) / 100) * stageSize.h : 0
  return {
    '--x': `${x.toFixed(1)}px`,
    '--y': `${y.toFixed(1)}px`,
    '--z': `${s.zPx.toFixed(1)}px`,
    '--ry-o': `${s.ryDeg.toFixed(2)}deg`,
    '--rx-o': `${s.rxDeg.toFixed(2)}deg`,
    '--rz-o': `${s.rzDeg.toFixed(2)}deg`,
    '--s': `${s.scale.toFixed(3)}`,
  }
}

function slotOfScreen(screenIndex: number): number {
  const n = count.value || 1
  const u = ((screenIndex + orbitOffset) % n + n) % n
  return Math.round(u) % n
}

/*
  Clicking any card or thumbnail glides it smoothly along the orbit track
  to land directly at the lead position (slot 0).
*/
function bringToFront(screenIndex: number) {
  dismissTutorial()
  const n = count.value
  if (n < 2) return

  const currentPos = ((screenIndex + orbitOffset) % n + n) % n
  const distToLead = Math.min(currentPos, n - currentPos)

  if (distToLead < 0.15 && targetOrbitOffset === null) {
    dive()
    return
  }

  let delta = -currentPos
  if (delta < -n / 2) delta += n
  if (delta > n / 2) delta -= n

  targetOrbitOffset = orbitOffset + delta
  userPaused.value = true
}

/* ---------------- auto rotation ---------------- */
const reduced =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const diving = ref(false)
const userPaused = ref(false)
const hovering = ref(false)
/* True while wheel-zoom is being held partway (see scroll-to-zoom below). */
const scrubbing = ref(false)
const onScreen = ref(true)
const docVisible = ref(true)
const wide = ref(typeof window !== 'undefined' ? window.matchMedia(WIDE).matches : true)

/* Every one of these has to be true for the continuous solar orbit to advance. */
const autoActive = computed(
  () =>
    !reduced &&
    wide.value &&
    !userPaused.value &&
    !hovering.value &&
    !scrubbing.value &&
    !diving.value &&
    onScreen.value &&
    docVisible.value &&
    count.value > 1
)

/* ---------------- first-time visitor tutorial ---------------- */
const STORAGE_KEY = 'am_orbit_tutorial_seen'
const showTutorial = ref(false)

function dismissTutorial() {
  if (!showTutorial.value) return
  showTutorial.value = false
  try {
    localStorage.setItem(STORAGE_KEY, 'true')
  } catch {}
}

function checkTutorial() {
  if (reduced || !wide.value) return
  try {
    const seen = localStorage.getItem(STORAGE_KEY)
    if (!seen) {
      setTimeout(() => {
        if (!diving.value && !scrubbing.value && !userPaused.value) {
          showTutorial.value = true
        }
      }, 700)
    }
  } catch {}
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    dismissTutorial()
  }
}

/* ---------------- parallax ---------------- */
const BASE_TILT = 6
const tiltEl = ref<HTMLElement>()
const backdropEl = ref<HTMLElement>()
const sectionEl = ref<HTMLElement>()
let frame = 0
let rx = BASE_TILT
let ry = 0
let targetX = BASE_TILT
let targetY = 0
let lastTime = performance.now()

function onPointerMove(e: PointerEvent) {
  if (diving.value || scrubbing.value) return
  targetY = (e.clientX / window.innerWidth - 0.5) * -12
  targetX = BASE_TILT + (e.clientY / window.innerHeight - 0.5) * 7
}

function tick() {
  const now = performance.now()
  const dt = Math.min(0.1, (now - lastTime) / 1000)
  lastTime = now

  // 1. Parallax
  const dx = targetX - rx
  const dy = targetY - ry
  if (Math.abs(dx) > 0.002 || Math.abs(dy) > 0.002) {
    rx += dx * 0.08
    ry += dy * 0.08
    if (tiltEl.value) {
      tiltEl.value.style.transform = `rotateX(${rx.toFixed(3)}deg) rotateY(${ry.toFixed(3)}deg)`
    }
    if (backdropEl.value) {
      const s = backdropEl.value.style
      s.setProperty('--px', `${(ry * -1.5).toFixed(1)}px`)
      s.setProperty('--py', `${((rx - BASE_TILT) * -2).toFixed(1)}px`)
    }
  }

  // 2. Orbit motion
  if (targetOrbitOffset !== null) {
    const diff = targetOrbitOffset - orbitOffset
    if (Math.abs(diff) > 0.002) {
      orbitOffset += diff * 0.08
    } else {
      orbitOffset = targetOrbitOffset
      targetOrbitOffset = null
    }
  } else if (autoActive.value) {
    orbitOffset += ORBIT_SPEED * dt
  }

  // 3. Determine lead index (closest to slot 0)
  const n = count.value
  if (n > 0) {
    let bestDist = Infinity
    let bestIdx = 0
    for (let k = 0; k < n; k++) {
      const u = ((k + orbitOffset) % n + n) % n
      const dist = Math.min(u, n - u)
      if (dist < bestDist) {
        bestDist = dist
        bestIdx = k
      }
    }
    if (leadIndex.value !== bestIdx) {
      leadIndex.value = bestIdx
    }
  }

  // 4. Update card transforms directly on DOM elements without Vue reactive overhead
  if (wide.value && cardEls.value.length > 0 && stageSize.w > 0) {
    cardEls.value.forEach((el, index) => {
      if (!el) return
      const u = ((index + orbitOffset) % n + n) % n
      const style = computeCardCSS(u, stageSize.w, stageSize.h)
      el.style.setProperty('--x', style.x)
      el.style.setProperty('--y', style.y)
      el.style.setProperty('--z', style.z)
      el.style.setProperty('--ry-o', style.ry)
      el.style.setProperty('--rx-o', style.rx)
      el.style.setProperty('--rz-o', style.rz)
      el.style.setProperty('--s', style.s)
    })
  }

  frame = requestAnimationFrame(tick)
}

/* ---------------- lifecycle ---------------- */
let observer: IntersectionObserver | undefined
let resizeObs: ResizeObserver | undefined
const wideQuery =
  typeof window !== 'undefined' ? window.matchMedia(WIDE) : null

function onWideChange(e: MediaQueryListEvent) {
  wide.value = e.matches
}
function onVisibility() {
  docVisible.value = !document.hidden
}

onMounted(() => {
  document.addEventListener('visibilitychange', onVisibility)
  wideQuery?.addEventListener('change', onWideChange)
  window.addEventListener('keydown', onKeydown)
  checkTutorial()

  /*
    Measure synchronously so the first reactive flush (pre-paint) already has
    real offsets — otherwise every plate renders at centre for one frame and
    then visibly blooms outward.
  */
  if (zoomEl.value) {
    stageSize.w = zoomEl.value.offsetWidth
    stageSize.h = zoomEl.value.offsetHeight
    resizeObs = new ResizeObserver(([entry]) => {
      stageSize.w = entry.contentRect.width
      stageSize.h = entry.contentRect.height
    })
    resizeObs.observe(zoomEl.value)
  }

  // Don't burn frames or rotate while the scene is scrolled out of view.
  if (sectionEl.value) {
    observer = new IntersectionObserver(
      ([entry]) => {
        onScreen.value = entry.isIntersecting
      },
      { threshold: 0.15 }
    )
    observer.observe(sectionEl.value)
  }

  if (reduced) return
  if (!window.matchMedia('(pointer: fine)').matches) return
  window.addEventListener('pointermove', onPointerMove, { passive: true })
  lastTime = performance.now()
  frame = requestAnimationFrame(tick)
})

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('keydown', onKeydown)
  document.removeEventListener('visibilitychange', onVisibility)
  wideQuery?.removeEventListener('change', onWideChange)
  observer?.disconnect()
  resizeObs?.disconnect()
  cancelAnimationFrame(frame)
})

/* ---------------- the dive ---------------- */
const zoomEl = ref<HTMLElement>()

/*
  Point the zoom's transform-origin at the lead plate's centre so scaling
  reads as the camera flying into that screen.
*/
function aimZoomAtLead() {
  const plate = tiltEl.value?.querySelector<HTMLElement>('.obj.is-lead .plate')
  if (!plate || !zoomEl.value) return
  const p = plate.getBoundingClientRect()
  const z = zoomEl.value.getBoundingClientRect()
  zoomEl.value.style.transformOrigin = `${p.left + p.width / 2 - z.left}px ${p.top + p.height / 2 - z.top}px`
}

function dive(reaim = true) {
  dismissTutorial()
  const project = lead.value?.project
  if (!project) return
  if (reaim && !reduced) aimZoomAtLead()
  // Flatten the tilt so the zoom flies straight in rather than off-axis.
  targetX = 0
  targetY = 0
  diving.value = true
  open(project.id)
}

/* ---------------- scroll-to-zoom ---------------- */
const DIVE_AFTER_PX = 640 // wheel travel that commits to the dive
const SCRUB_SPAN = 1.6 // growth across that travel (1× → 2.6×)
let scrubPx = 0
let scrubLead: HTMLElement | null = null
let scrubLeadBaseW = 0

function releaseScrubLead() {
  scrubLead?.style.removeProperty('width')
  scrubLead = null
}

function onWheel(e: WheelEvent) {
  if (!wide.value || reduced || diving.value) return
  dismissTutorial()
  const engaged = scrubPx > 0
  // Heading down while not zoomed is ordinary reading — leave it to the page.
  if (e.deltaY > 0 && !engaged) return
  // Only intercept upward input while the page is actually at the top;
  // otherwise scrolling back up through the page would snag on the scene.
  if (!engaged && window.scrollY > 4) return
  e.preventDefault()

  // Normalise: Firefox reports lines (mode 1), some setups pages (mode 2).
  // Negated so that upward wheel (negative deltaY) accumulates zoom.
  const px = -e.deltaY * (e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 300 : 1)
  if (!engaged) {
    aimZoomAtLead()
    scrubLead = tiltEl.value?.querySelector<HTMLElement>('.obj.is-lead') ?? null
    scrubLeadBaseW = scrubLead?.offsetWidth ?? 0
    targetX = 0
    targetY = 0
  }
  scrubPx = Math.max(0, scrubPx + px)

  if (scrubPx >= DIVE_AFTER_PX) {
    scrubPx = 0
    scrubbing.value = false
    dive(false)
    return
  }

  scrubbing.value = scrubPx > 0
  if (scrubLead && scrubLeadBaseW) {
    if (scrubPx > 0) {
      const s = 1 + (scrubPx / DIVE_AFTER_PX) * SCRUB_SPAN
      scrubLead.style.width = `${(scrubLeadBaseW * s).toFixed(1)}px`
    } else {
      releaseScrubLead()
    }
  }
  if (!scrubbing.value) {
    targetX = BASE_TILT
    targetY = 0
  }
}

function abandonScrub() {
  if (!scrubbing.value) return
  scrubPx = 0
  scrubbing.value = false
  releaseScrubLead()
  targetX = BASE_TILT
  targetY = 0
}

function onLeaveViewport() {
  hovering.value = false
  abandonScrub()
}

/* Returning from the detail view reverses the zoom and restores the tilt. */
watch(activeId, (id) => {
  if (!id) {
    diving.value = false
    releaseScrubLead()
    targetX = BASE_TILT
    targetY = 0
  }
})

const caption = computed(() => {
  const screen = lead.value
  if (!screen) return ''
  const n = String(screen.ordinal).padStart(2, '0')
  return `${n} — ${screen.project.shortTitle ?? screen.project.title}`
})
</script>

<template>
  <section id="home" ref="sectionEl" class="scene-section" :data-diving="diving" :data-scrubbing="scrubbing">
    <!-- ---------- backdrop: floating outline words ---------- -->
    <div ref="backdropEl" class="backdrop" aria-hidden="true">
      <div v-for="layer in blobLayers" :key="layer.cls" class="layer" :class="layer.cls" :style="{ '--d': layer.d }">
        <span v-for="blob in layer.items" :key="blob.text" class="blob" :style="{
          top: blob.top,
          left: blob.left,
          '--size': blob.size,
          '--rot': blob.rot,
          '--dur': blob.dur,
          '--delay': blob.delay,
        }">{{ blob.text }}</span>
      </div>
    </div>

    <!-- ---------- identity ---------- -->
    <div class="copy">
      <div class="face">
        <img :src="portrait" alt="Portrait of Abraham Michael" width="88" height="88" />
        <span class="chip" aria-hidden="true"></span>
      </div>

      <h1 class="display display-2 name">{{ personalInfo.name.full }}</h1>
      <!-- kept to one line on purpose; the full title is in About -->
      <p class="eyebrow role">{{ personalInfo.taglines.primary }} · Neo</p>

      <p class="bio">
        Software engineer specializing in high-performance mobile applications and full-stack systems
        across Flutter, React Native, and native Android, with deployed software in logistics,
        transportation, healthcare, and finance.
      </p>

      <p class="status meta">
        <i aria-hidden="true"></i>{{ personalInfo.contact.availability }}
      </p>

      <div class="actions">
        <a class="cv" href="https://flowcv.com/resume/i6mptf4q4rtb" target="_blank">
          Resume <span aria-hidden="true"></span>
        </a>
        <a v-for="social in socialLinks" :key="social.name" class="soc" :href="social.href"
          :aria-label="social.ariaLabel" :target="social.href.startsWith('http') ? '_blank' : undefined"
          :rel="social.href.startsWith('http') ? 'noopener noreferrer' : undefined">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path :d="socialIconPaths[social.icon]" />
          </svg>
        </a>
      </div>

      <div class="invite">
        <span class="ring" aria-hidden="true">↵</span>
        <span v-if="scrubbing" class="eyebrow">Keep scrolling to <b>step inside</b></span>
        <span v-else class="eyebrow">Scroll up or click the front screen to <b>step inside</b></span>
      </div>
    </div>

    <!-- ---------- the scene ---------- -->
    <div class="viewport" @pointerenter="hovering = true" @pointerleave="onLeaveViewport" @wheel="onWheel">
      <div ref="zoomEl" class="zoom">
        <div ref="tiltEl" class="tilt">
          <button
            v-for="(screen, screenIndex) in screens"
            :key="screen.project.id"
            :ref="(el) => { if (el) cardEls[screenIndex] = el as HTMLElement }"
            class="obj"
            :class="[
              screenIndex === leadIndex ? 'is-lead' : '',
              screenIndex === leadIndex && showTutorial ? 'has-tut-highlight' : '',
              screen.device === 'phone' ? 'is-phone' : 'is-desktop',
            ]"
            :data-slot="slotOfScreen(screenIndex)"
            :style="initialCardStyle(screenIndex)"
            :aria-label="screenIndex === leadIndex
              ? `Open ${screen.project.title} case study`
              : `Bring ${screen.project.title} to the front`
            "
            @click="bringToFront(screenIndex)"
          >
            <span class="cast" aria-hidden="true"></span>
            <span class="plate">
              <span class="shot">
                <img :src="screen.src" :alt="screen.project.title" :loading="screenIndex === 0 ? 'eager' : 'lazy'"
                  :fetchpriority="screenIndex === 0 ? 'high' : 'low'" decoding="async" />
              </span>
            </span>
            <span v-if="screen.device === 'desktop'" class="stand" aria-hidden="true"></span>
          </button>
        </div>
      </div>

      <!-- ---------- onboarding tutorial for first-time visitors ---------- -->
      <Transition name="tut-fade">
        <div
          v-if="showTutorial"
          class="orbit-tutorial"
          role="status"
          aria-live="polite"
          aria-label="Scene interaction tutorial"
        >
          <div class="tut-card">
            <div class="tut-gesture" aria-hidden="true">
              <div class="tut-mouse">
                <span class="tut-wheel"></span>
              </div>
              <span class="tut-chevron">↑</span>
            </div>

            <div class="tut-body">
              <div class="tut-tag">
                <span class="tut-dot"></span>
                <span>Interactive 3D Orbit</span>
              </div>
              <p class="tut-heading">Scroll up or click front card to dive in</p>
              <p class="tut-sub">
                Screens orbit continuously · Click any project to bring it forward
              </p>
            </div>

            <button
              type="button"
              class="tut-btn"
              aria-label="Dismiss tutorial"
              @click.stop="dismissTutorial"
            >
              Got it
            </button>
          </div>
        </div>
      </Transition>

      <p class="caption meta" aria-live="polite">{{ caption }}</p>
    </div>

    <!-- ---------- rail ---------- -->
    <div class="rail">
      <div class="thumbs" role="tablist" aria-label="Choose a project">
        <button
          v-for="(screen, screenIndex) in screens"
          :key="`rail-${screen.project.id}`"
          class="thumb"
          :class="{ 'is-current': screenIndex === leadIndex }"
          role="tab"
          :aria-selected="screenIndex === leadIndex"
          :aria-label="screen.project.title"
          @click="bringToFront(screenIndex)"
        >
          <img :src="screen.src" alt="" loading="lazy" decoding="async" />
        </button>
      </div>

      <!--
        WCAG 2.2.2: anything that moves automatically for more than five
        seconds needs a way to stop it.
      -->
      <button
        v-if="!reduced && wide && count > 1"
        class="playpause"
        :aria-label="userPaused ? 'Resume automatic rotation' : 'Pause automatic rotation'"
        :aria-pressed="userPaused"
        @click="userPaused = !userPaused"
      >
        <svg v-if="userPaused" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M8 5v14l11-7z" />
        </svg>
        <svg v-else viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M7 5h3v14H7zM14 5h3v14h-3z" />
        </svg>
      </button>

      <button
        v-if="!reduced && wide"
        class="help-hint"
        :aria-label="showTutorial ? 'Hide interaction tutorial' : 'Show interaction tutorial'"
        title="Orbit interaction tips"
        @click="showTutorial = !showTutorial"
      >
        ?
      </button>

      <a href="#work" class="all-work meta">All work →</a>
    </div>
  </section>
</template>

<style scoped>
/*
  Two columns on desktop: the copy owns the left, the scene owns the right.
  Nothing overlaps, so the headline can be as large as it wants to be.
*/
.scene-section {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: clamp(2rem, 5vh, 3rem);
  padding: clamp(6rem, 14vh, 9rem) 0 clamp(2rem, 6vh, 3.5rem);
  overflow: hidden;
}

@media (min-width: 1000px) {
  .scene-section {
    /*
      The scene gets the lion's share. Not more than this, though: below
      ~0.75fr the headline drops to two or three words a line and the
      invite wraps.
    */
    grid-template-columns: minmax(0, 0.78fr) minmax(0, 1.22fr);
    gap: clamp(1rem, 2vw, 2rem);
    min-height: 100svh;
    padding-block: clamp(5rem, 11vh, 7rem) clamp(1.5rem, 4vh, 2.5rem);
  }

  /*
    Span both rows (scene + rail) and centre against the whole thing —
    otherwise the copy sits in row 1 and leaves an empty cell beneath it.
  */
  .copy {
    grid-row: 1 / 3;
    align-self: center;
  }
}

/*
  Past ~1440px the fr split stops making sense: the identity card is capped
  at ~40ch, so a proportional column just grows dead space between the card
  and the scene. Pin the copy to its content width and hand everything else
  to the scene — the empty space moves from the centre of the hero out to
  where the plates can use it.
*/
@media (min-width: 1440px) {
  .scene-section {
    grid-template-columns: minmax(0, 36rem) minmax(0, 1fr);
  }
}

/* ---------------- backdrop: floating outline words ---------------- */
/*
  Outline type behind everything. z-index -1 keeps it under the in-flow
  content without touching sibling stacking (it still paints above the body
  texture, since ancestors' backgrounds render first).

  Stroke opacity is the restraint budget — if it ever reads as content, lower
  the per-layer stroke, not the size. Big-and-faint stays atmospheric;
  small-and-visible becomes clutter.
*/
.backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
  transition: opacity 0.4s ease;
}

/* one parallax layer per depth; --px/--py come from the rAF loop */
.layer {
  position: absolute;
  inset: 0;
  translate: calc(var(--px, 0px) * var(--d)) calc(var(--py, 0px) * var(--d));
  will-change: translate;
}

.blob {
  position: absolute;
  /* the anchor is the word's centre, so `left`/`top` read as placement */
  translate: -50% -50%;
  white-space: nowrap;
  font-family: var(--font-display);
  font-size: var(--size);
  font-weight: 600;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  line-height: 1;
  color: transparent;
  rotate: var(--rot, 0deg);
  /*
    `translate` is animated and `rotate` is static — using the individual
    transform properties instead of the `transform` shorthand keeps the bob
    from clobbering the rotation (and vice versa).
  */
  animation: bob var(--dur, 18s) var(--delay, 0s) ease-in-out infinite;
}

/* depth reads through weight of line, not just size */
.far .blob {
  -webkit-text-stroke: 1px color-mix(in srgb, var(--c-ink) 11%, transparent);
}

.mid .blob {
  -webkit-text-stroke: 1.1px color-mix(in srgb, var(--c-ink) 14%, transparent);
}

.near .blob {
  -webkit-text-stroke: 1.4px color-mix(in srgb, var(--c-ink) 17%, transparent);
}

/*
  Bob in place — nothing travels. Compounds with the -50%/-50% centring, so
  the keyframes carry it through.
*/
@keyframes bob {

  0%,
  100% {
    translate: -50% -50%;
  }

  50% {
    translate: -50% calc(-50% - 12px);
  }
}

[data-diving='true'] .backdrop,
[data-scrubbing='true'] .backdrop {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .blob {
    animation: none;
  }

  .layer {
    translate: none;
  }
}

@media (max-width: 999px) {
  .backdrop {
    display: none;
  }
}

/* ---------------- identity ---------------- */
.copy {
  padding-inline: var(--gutter);
  min-width: 0;
  transition: opacity 0.45s ease;
}

/* square portrait with the same accent chip as the wordmark — one mark, twice */
.face {
  position: relative;
  width: 88px;
  margin-bottom: 1.4rem;
}

.face img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  object-position: center top;
  border: 1px solid var(--c-rule);
  border-radius: 4px;
  filter: grayscale(1) contrast(1.05);
  transition: filter 0.45s ease;
}

.face:hover img {
  filter: none;
}

.face .chip {
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 10px;
  height: 10px;
  background: var(--c-accent);
}

.name {
  margin: 0 0 0.5rem;
}

.role {
  margin: 0 0 1.2rem;
}

.bio {
  margin: 0 0 1.4rem;
  color: var(--c-ink-2);
  font-size: 0.98rem;
  line-height: 1.62;
  max-width: 40ch;
}

.bio em {
  font-style: normal;
  color: var(--c-accent);
}

.status {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0 0 1.5rem;
  color: var(--c-ink);
}

.status i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--c-ok);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--c-ok) 22%, transparent);
}

.actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.cv {
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.55rem 1rem;
  border: 1px solid var(--c-rule-strong);
  border-radius: 3px;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--c-ink);
  transition: border-color 0.2s ease, color 0.2s ease;
}

.cv span {
  transition: translate 0.3s var(--ease-out-quint);
}

.cv:hover {
  border-color: var(--c-accent);
  color: var(--c-accent);
}

.cv:hover span {
  translate: 0 2px;
}

.soc {
  width: 2.2rem;
  height: 2.2rem;
  display: grid;
  place-items: center;
  border: 1px solid var(--c-rule);
  border-radius: 50%;
  color: var(--c-ink-2);
  transition: color 0.2s ease, border-color 0.2s ease, translate 0.2s ease;
}

.soc svg {
  width: 0.95rem;
  height: 0.95rem;
}

.soc:hover {
  color: var(--c-accent);
  border-color: var(--c-accent);
  translate: 0 -2px;
}

.invite {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 2.2rem;
}

/* keep the hint on one line — wrapped, it reads as a paragraph, not a cue */
.invite .eyebrow {
  text-wrap: nowrap;
}

@media (max-width: 420px) {
  .invite .eyebrow {
    text-wrap: wrap;
  }
}

.invite b {
  color: var(--c-accent);
  font-weight: 400;
  position: relative;
}

.invite b::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -3px;
  height: 1px;
  background: var(--c-accent);
  transform-origin: left;
  animation: sweep 2.8s var(--ease-dive) infinite;
}

@keyframes sweep {

  0%,
  70%,
  100% {
    scale: 0 1;
  }

  18%,
  52% {
    scale: 1 1;
  }
}

.ring {
  width: 26px;
  height: 26px;
  flex: none;
  border: 1px solid var(--c-rule);
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 0.7rem;
  color: var(--c-ink-2);
}

/* ---------------- stage ---------------- */
.viewport {
  position: relative;
  min-width: 0;
  perspective: 1500px;
  perspective-origin: 50% 45%;
}

/*
  Dive layer. Retargeted exactly once per dive, so it is the only element in
  the scene allowed a transform transition.
*/
.zoom {
  position: relative;
  height: min(76vh, 45rem);
  transform-style: preserve-3d;
  pointer-events: none;
  transition: transform 0.9s var(--ease-dive), opacity 0.35s ease;
}

/*
  Parallax layer. Driven per-frame from a rAF loop, so transitions on
  transform are forbidden here — the JS lerp is the easing.

  Both layers keep pointer-events: none: their planes sit at z=0, so plates
  pushed to negative translateZ render behind them, and a hit-testable
  container would swallow every click meant for the plates.
*/
.tilt {
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
  transform: rotateX(6deg);
  pointer-events: none;
  will-change: transform;
}

[data-diving='true'] .zoom {
  transform: scale(7);
  opacity: 0;
}

[data-diving='true'] .copy,
[data-diving='true'] .rail,
[data-diving='true'] .caption {
  opacity: 0;
}

[data-diving='true'] .obj:not(.is-lead) {
  opacity: 0;
}

/*
  Scroll-to-zoom scrub: the wheel grows the lead plate's layout width (see
  onWheel) — .zoom itself is never transformed during a scrub, only by the
  dive, so no per-event transition juggling is needed here anymore.
*/
[data-scrubbing='true'] .obj:not(.is-lead) {
  opacity: 0.25;
}

[data-scrubbing='true'] .copy {
  opacity: 0.35;
}

/*
  Slot geometry only. The orbit is long and smooth; the hover lift lives on
  .plate so it can stay short without the two fighting over one transition.
  No z-index: inside preserve-3d the browser sorts by real depth, and an
  un-interpolatable z-index would pop mid-orbit.
*/
.obj {
  position: absolute;
  /*
    Every plate anchors at the stage centre; its slot position arrives as a
    px offset INSIDE the transform (--x/--y). Binding left/top per slot broke
    the orbit: those properties sat outside the transform transition, so each
    plate snapped to its new spot and then rotated in place.
  */
  left: 50%;
  top: 50%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  pointer-events: auto;
  transform-style: preserve-3d;
  transform: translate(-50%, -50%) translate3d(var(--x, 0px), var(--y, 0px), var(--z)) rotateY(var(--ry-o)) rotateX(var(--rx-o)) rotateZ(var(--rz-o)) scale(var(--s));
  /* no filter here — see the grouping-property note on the img dim below.
     width is the scroll-zoom channel: 0.12s smooths each wheel notch and
     glides the release, without reading as inertia. */
  transition: opacity 0.5s ease, width 0.12s linear;
}

.obj.is-desktop {
  width: min(28vw, 440px);
}

.obj.is-phone {
  width: min(12.5vw, 170px);
}

/*
  The background dim lives on the IMG, not the .obj: filter is a grouping
  property, and on .obj it silently flattened the preserve-3d subtree — which
  killed the hover straighten below (the counter-rotation projected flat and
  translateZ did nothing). The img has no 3D children, so it's safe there.
*/
.obj:not(.is-lead) .plate img {
  filter: saturate(0.7) brightness(0.94);
  transition: filter 0.6s ease;
}

.obj:not(.is-lead):hover .plate img,
.obj:not(.is-lead):focus-visible .plate img {
  filter: none;
}

/*
  Dark mode: the screenshots are light product UIs and glare at full
  brightness on the near-black surface. Dimmed in CSS, not baked into the
  files — one set of images serves both themes and the level stays tunable.
  The lead sits at ~0.72; background plates drop further so depth still
  reads; hover restores a plate to lead level, never to full white.
*/
[data-theme='dark'] .shot img {
  filter: brightness(0.72) contrast(1.05) saturate(0.95);
  transition: filter 0.6s ease;
}
[data-theme='dark'] .obj:not(.is-lead) .plate img {
  filter: saturate(0.55) brightness(0.5);
}
[data-theme='dark'] .obj:not(.is-lead):hover .plate img,
[data-theme='dark'] .obj:not(.is-lead):focus-visible .plate img {
  filter: brightness(0.72) contrast(1.05) saturate(0.95);
}
/* the white glare gradient reads as a smudge on dimmed screens */
[data-theme='dark'] .plate::after {
  opacity: 0.35;
}
[data-theme='dark'] .thumb img {
  filter: brightness(0.7);
}

/*
  Pointer on a screen: cancel the slot rotation (inverse order, negated
  angles) so the shot stands straight and becomes readable. Lives on .plate —
  the orbit on .obj keeps its slow transition, this gets a quick one.
  Background plates also come well forward; the lead is already in front.
*/
.obj:hover .plate,
.obj:focus-visible .plate {
  transform: rotateZ(calc(var(--rz-o) * -1)) rotateX(calc(var(--rx-o) * -1)) rotateY(calc(var(--ry-o) * -1));
}

.obj:not(.is-lead):hover .plate,
.obj:not(.is-lead):focus-visible .plate {
  transform: rotateZ(calc(var(--rz-o) * -1)) rotateX(calc(var(--rx-o) * -1)) rotateY(calc(var(--ry-o) * -1)) translateZ(140px);
}

/* the screen itself */
.plate {
  position: relative;
  display: block;
  overflow: hidden;
  background: #0f100e;
  border-radius: 11px;
  padding: 8px;
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.14), var(--shadow-plate);
  /* the hover straighten lives on this layer — short, independent ease */
  transition: transform 0.4s var(--ease-out-quint);
}

.is-phone .plate {
  border-radius: 18px;
  padding: 4px;
}

.shot {
  display: block;
  aspect-ratio: 16 / 10;
  border-radius: 5px;
  overflow: hidden;
}

.is-phone .shot {
  aspect-ratio: 9 / 17;
  border-radius: 15px;
}

/*
  No pre-raster trick here — an earlier attempt laid the image out at 200%
  and scaled it back down, on the theory that this banked a 2× texture. It
  doesn't: the compositor rasterizes the whole LAYER once at one scale, and an
  over-sized image simply gets downsampled at raster time. The lever that
  actually matters is the layer's raster scale, handled by dropping
  `will-change` during a scrub (see below).
*/
.shot img {
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  object-position: left top;
}

.is-phone .shot img {
  aspect-ratio: 9 / 17;
}

/* glare — sells the glass without a gradient soup */
.plate::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(115deg,
      rgb(255 255 255 / 0.15) 0%,
      rgb(255 255 255 / 0.04) 34%,
      transparent 58%);
  mix-blend-mode: screen;
}

.stand {
  position: absolute;
  left: 50%;
  bottom: -13px;
  translate: -50% 0;
  width: 20%;
  height: 13px;
  background: linear-gradient(#26241f, #151410);
  border-radius: 0 0 4px 4px;
  /*
    Rendered on every desktop plate but only visible on the lead — a v-if
    made it pop in/out instantly, which read as part of the "snap". It fades
    in late so it appears as the plate lands, and drops fast on departure.
  */
  opacity: 0;
  transition: opacity 0.25s ease;
}

.is-lead .stand {
  opacity: 1;
  transition: opacity 0.35s ease 0.85s;
}

.stand::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -5px;
  translate: -50% 0;
  width: 280%;
  height: 6px;
  background: linear-gradient(#211f1a, #14130f);
  border-radius: 3px;
}

/* contact shadow on the paper */
.cast {
  position: absolute;
  left: 50%;
  bottom: -7%;
  translate: -50% 0;
  width: 110%;
  height: 16%;
  background: radial-gradient(ellipse at center,
      rgb(16 17 20 / 0.32),
      transparent 70%);
  filter: blur(9px);
  transform: rotateX(72deg) translateZ(-30px);
  pointer-events: none;
}

[data-theme='dark'] .cast {
  background: radial-gradient(ellipse at center, rgb(0 0 0 / 0.62), transparent 70%);
}

.caption {
  margin: 1.2rem 0 0;
  padding-inline: var(--gutter);
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--c-ink-3);
  transition: opacity 0.4s ease;
}

@media (min-width: 1000px) {

  /*
    Kept in normal flow beneath the stage. Absolutely positioning it inside
    .viewport dropped it on top of whichever plate occupied the lower left.
  */
  .caption {
    padding-inline: 0;
  }
}

/* ---------------- rail ---------------- */
.rail {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding-inline: var(--gutter);
  padding-bottom: 0.4rem;
  transition: opacity 0.4s ease;
  min-width: 0;
}

@media (min-width: 1000px) {
  .rail {
    grid-column: 2;
    padding-left: 0;
  }
}

.thumbs {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  overflow-x: auto;
  min-width: 0;
}

.thumb {
  flex: none;
  width: 4.2rem;
  padding: 0;
  border: 1px solid var(--c-rule);
  border-radius: 3px;
  background: var(--c-surface-sunken);
  overflow: hidden;
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.25s ease, border-color 0.25s ease;
}

.thumb img {
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  object-position: left top;
}

.thumb:hover {
  opacity: 0.85;
}

.thumb.is-current {
  opacity: 1;
  border-color: var(--c-accent);
}

.playpause {
  flex: none;
  width: 1.9rem;
  height: 1.9rem;
  display: grid;
  place-items: center;
  padding: 0;
  background: none;
  border: 1px solid var(--c-rule);
  border-radius: 50%;
  color: var(--c-ink-3);
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.playpause svg {
  width: 0.7rem;
  height: 0.7rem;
}

.playpause:hover {
  color: var(--c-accent);
  border-color: var(--c-accent);
}

.help-hint {
  flex: none;
  width: 1.9rem;
  height: 1.9rem;
  display: grid;
  place-items: center;
  padding: 0;
  background: none;
  border: 1px solid var(--c-rule);
  border-radius: 50%;
  color: var(--c-ink-3);
  font-family: var(--font-mono);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.help-hint:hover {
  color: var(--c-accent);
  border-color: var(--c-accent);
}

.all-work {
  flex: none;
  margin-left: auto;
  color: var(--c-ink-2);
  border-bottom: 1px solid var(--c-rule);
  padding-bottom: 1px;
}

.all-work:hover {
  color: var(--c-accent);
  border-color: var(--c-accent);
}

/* ---------------- first-time visitor tutorial ---------------- */
.has-tut-highlight .plate {
  box-shadow: 0 0 0 2px var(--c-accent), 0 0 24px var(--c-accent-tint), var(--shadow-plate);
  animation: tut-pulse 2.4s ease-in-out infinite;
}

@keyframes tut-pulse {
  0%, 100% {
    box-shadow: 0 0 0 2px var(--c-accent), 0 0 16px var(--c-accent-tint), var(--shadow-plate);
  }
  50% {
    box-shadow: 0 0 0 3px var(--c-accent), 0 0 32px color-mix(in srgb, var(--c-accent) 35%, transparent), var(--shadow-plate);
  }
}

.orbit-tutorial {
  position: absolute;
  left: 50%;
  bottom: 2.8rem;
  translate: -50% 0;
  z-index: 20;
  pointer-events: auto;
  max-width: min(94%, 480px);
  width: max-content;
}

.tut-card {
  display: flex;
  align-items: center;
  gap: 1.1rem;
  padding: 0.85rem 1.15rem;
  background: color-mix(in srgb, var(--c-surface-raised) 88%, transparent);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid color-mix(in srgb, var(--c-accent) 35%, var(--c-rule));
  border-radius: 12px;
  box-shadow: 0 16px 36px -12px rgb(0 0 0 / 0.28), 0 0 0 1px color-mix(in srgb, var(--c-accent) 12%, transparent);
}

.tut-gesture {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  flex: none;
}

.tut-mouse {
  width: 22px;
  height: 32px;
  border: 2px solid var(--c-ink);
  border-radius: 11px;
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 4px;
}

.tut-wheel {
  width: 3px;
  height: 6px;
  background: var(--c-accent);
  border-radius: 2px;
  animation: tut-wheel-anim 1.8s cubic-bezier(0.65, 0, 0.35, 1) infinite;
}

@keyframes tut-wheel-anim {
  0% {
    translate: 0 6px;
    opacity: 0;
  }
  25% {
    opacity: 1;
  }
  75% {
    translate: 0 -2px;
    opacity: 1;
  }
  100% {
    translate: 0 -4px;
    opacity: 0;
  }
}

.tut-chevron {
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  color: var(--c-accent);
  animation: tut-chev-anim 1.8s ease-in-out infinite;
}

@keyframes tut-chev-anim {
  0%, 100% {
    translate: 0 2px;
    opacity: 0.4;
  }
  50% {
    translate: 0 -2px;
    opacity: 1;
  }
}

.tut-body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.tut-tag {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-mono);
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--c-accent);
  font-weight: 600;
}

.tut-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--c-accent);
  box-shadow: 0 0 6px var(--c-accent);
}

.tut-heading {
  margin: 0;
  font-family: var(--font-display);
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--c-ink);
  line-height: 1.3;
}

.tut-sub {
  margin: 0;
  font-size: 0.75rem;
  color: var(--c-ink-2);
  line-height: 1.35;
}

.tut-btn {
  flex: none;
  padding: 0.45rem 0.85rem;
  border: 1px solid var(--c-rule-strong);
  border-radius: 6px;
  background: var(--c-surface-sunken);
  color: var(--c-ink);
  font-family: var(--font-sans);
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.tut-btn:hover {
  background: var(--c-accent);
  color: var(--c-accent-ink);
  border-color: var(--c-accent);
}

/* Transition */
.tut-fade-enter-active,
.tut-fade-leave-active {
  transition: opacity 0.4s ease, translate 0.4s var(--ease-out-quint);
}

.tut-fade-enter-from,
.tut-fade-leave-to {
  opacity: 0;
  translate: -50% 12px;
}

[data-diving='true'] .orbit-tutorial,
[data-scrubbing='true'] .orbit-tutorial {
  opacity: 0;
  pointer-events: none;
}

/* ============================================================
   Below 1000px the 3D composition would crowd, so the scene
   collapses to the lead screen and the rail does the switching.
   Auto rotation is off there: with no 3D motion to carry it, a
   swap would read as a glitch rather than an orbit.
   ============================================================ */
@media (max-width: 999px) {
  .viewport {
    padding-inline: var(--gutter);
    perspective: none;
  }

  .zoom {
    height: auto;
    transform: none !important;
    transition: opacity 0.4s ease;
  }

  .tilt {
    position: static;
    transform: none !important;
  }

  .obj {
    position: relative;
    left: auto !important;
    top: auto !important;
    width: 100% !important;
    transform: none !important;
    display: block;
  }

  .obj:not(.is-lead) {
    display: none;
  }

  .shot,
  .is-phone .shot {
    aspect-ratio: 16 / 10;
  }

  .shot img,
  .is-phone .shot img {
    aspect-ratio: 16 / 10;
  }

  .cast,
  .stand {
    display: none;
  }

  [data-diving='true'] .zoom {
    opacity: 0;
    transform: none !important;
  }

  .invite b::after {
    animation: none;
    scale: 1 1;
  }

  .all-work {
    margin-left: 0.4rem;
  }
}

@media (prefers-reduced-motion: reduce) {

  .zoom,
  .obj,
  .plate {
    transition-duration: 0.01ms;
  }

  [data-diving='true'] .zoom {
    transform: none;
  }

  .invite b::after {
    animation: none;
    scale: 1 1;
  }
}
</style>
